import { useForm, useWatch } from "react-hook-form";
import {
  SelectOption,
  SelectWithValidation,
} from "../../../../components/form/SelectWithValidation";
import { IOrganisation, IUser } from "../../../../services/admin/admin.service";
import userService from "../../../../services/user/user.service";
import { useState } from "react";
import { Button, Dialog, Typography } from "@safetyculture/sc-web-ui";
import { validation_rules } from "../../../../components/form/_validation_rules";
import { useUserSelectOptions } from "../../../../hooks/queries/select_options/useUserSelectOptions.query";
import toastEvent from "../../../../components/util/toast/toast.event";
import { Spacing } from "../../../../components/layout/Spacing";

interface OrgUserModalProps {
  org_data: IOrganisation;
  onClose: () => void;
  onConfirm?: () => void;
}

export const OrgUserModal = ({
  org_data,
  onClose,
  onConfirm,
}: OrgUserModalProps) => {
  const form = useForm<UserIdForm>({ mode: "all" });
  const [org_search_term, setOrgSearchTerm] = useState<string>("");
  const [query_key, setQueryKey] = useState<string>(Date.now().toString());
  const user_select_options = useUserSelectOptions({
    user_search_term: org_search_term,
    filter_out_users_from_org_id: org_data?.id,
    query_key,
  });

  const handleSubmit = async (formData: UserIdForm): Promise<void> => {
    let result: boolean = false;
    if (formData.user.meta?.organisations?.[0]?.id) {
      result = await userService.updateUserOrg({
        org_id: org_data.id,
        user_id: formData?.user.value,
        old_org_id: formData.user.meta?.organisations[0]?.id,
      });
    } else {
      result = await userService.addUserOrg({
        org_id: org_data.id,
        user_id: formData?.user.value,
      });
    }
    if (result) {
      setQueryKey(Date.now().toString());
      onConfirm && onConfirm();
      onClose();
    }
  };

  const form_values = useWatch({
    control: form.control,
  });

  return (
    <Dialog
      isOpen={true}
      onClose={onClose}
      zIndex={10000}
      size="large"
      showCloseButton={true}
    >
      <Dialog.Header>
        <Dialog.Title>Assign user to organisation</Dialog.Title>
      </Dialog.Header>

      <Dialog.Body>
        <Typography variant="bodyMedium">
          Users are only able to join one organisation.
        </Typography>
        <SelectWithValidation
          control={form.control}
          multiple={false}
          rules={{
            required: validation_rules.select.required(
              "Select a user to assign",
            ),
          }}
          label="User"
          name="user"
          data={user_select_options.data}
          isLoading={user_select_options.isLoading}
          searchable={setOrgSearchTerm}
          margin={{ top: 24 }}
          placeholder="Find user by id, first name, last name, or email"
        />
        <Spacing $height={24} />
        {!!form_values.user?.meta?.organisations?.[0]?.name && (
          <Typography variant="bodyMedium" color="info.text.default">
            You are about to move {form_values.user?.label} from{" "}
            {form_values.user?.meta?.organisations?.[0].name} to {org_data.name}
            .
          </Typography>
        )}
      </Dialog.Body>

      <Dialog.Footer>
        <Button
          onClick={form.handleSubmit(handleSubmit)}
          variant="primary"
          disabled={user_select_options.isLoading}
          isLoading={form.formState.isSubmitting}
        >
          {form_values.user?.meta?.organisations?.[0]
            ? "Move user"
            : "Assign user"}
        </Button>
        <Button onClick={onClose} variant="secondary">
          Cancel
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};

type UserIdForm = { user: SelectOption<IUser> };
